<template>
  <BaseModal
    :is-visible="isVisible"
    title="Delete Exam"
    subtitle="Deleting Exam"
    :icon="Trash2"
    size="lg"
    type="danger"
    :show-footer="true"
    @close="closeModal"
    role="alertdialog"
    aria-labelledby="delete-exam-title"
    aria-describedby="delete-exam-description"
  >
    <div class="space-y-6">
      <!-- Warning Message -->
      <div class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
        <div class="flex items-start">
          <svg
            class="h-6 w-6 text-red-500 mt-0.5 mr-3 flex-shrink-0"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fill-rule="evenodd"
              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
              clip-rule="evenodd"
            />
          </svg>
          <div>
            <h4 class="text-sm font-semibold text-red-800 dark:text-red-200" id="delete-exam-title">
              Exam will be removed from the system
            </h4>
            <p class="text-sm text-red-700 dark:text-red-300 mt-1" id="delete-exam-description">
              This will permanently delete all exam content and associated data. This action can't be undone.
            </p>
          </div>
        </div>
      </div>

      <!-- Exam Information -->
      <div class="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
        <h4 class="text-sm font-semibold text-gray-900 dark:text-white mb-3">
          Exam Details
        </h4>
        <div class="space-y-2">
          <div class="flex justify-between">
            <span class="text-sm text-gray-600 dark:text-gray-400">Exam ID:</span>
            <span class="text-sm font-medium text-gray-900 dark:text-white text-right">
              {{ exam?.exam_id }}
            </span>
          </div>
          <div class="flex justify-between">
            <span class="text-sm text-gray-600 dark:text-gray-400">Exam Name:</span>
            <span 
              class="text-sm font-medium text-gray-900 dark:text-white text-right"
              :title="isTextTruncated(exam?.exam_name || '') ? exam?.exam_name : undefined"
            >
              {{ truncateText(exam?.exam_name || '') }}
            </span>
          </div>
          <div class="flex justify-between">
            <span class="text-sm text-gray-600 dark:text-gray-400">Lesson Name:</span>
            <span 
              class="text-sm font-medium text-gray-900 dark:text-white text-right" 
              :title="isTextTruncated(exam?.lesson?.name || '(No lesson)') ? (exam?.lesson?.name || '(No lesson)') : undefined"
            >
              {{ truncateText(exam?.lesson?.name || '(No lesson)') }}
            </span>
          </div>
        </div>
      </div>

      <!-- Confirmation Input -->
      <div class="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl p-4">
        <h4 class="text-sm font-semibold text-yellow-800 dark:text-yellow-200 mb-2">
          Confirmation Required
        </h4>
        <p class="text-sm text-yellow-700 dark:text-yellow-300 mb-3">
          Type <strong>DELETE</strong> to confirm this action:
        </p>
        <input
          v-model="confirmationText"
          type="text"
          placeholder="Type DELETE to confirm"
          class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:focus:ring-blue-400 dark:focus:border-blue-400 transition-all duration-200"
          @input="validateConfirmation"
          @keydown.enter="handleDelete"
          aria-label="Type DELETE to confirm deletion"
        />
      </div>
    </div>

    <!-- Footer buttons -->
    <template #footer>
      <BaseButton
        text="Cancel"
        variant="secondary"
        :disabled="isDeleting"
        @click="closeModal"
      />
      <BaseButton
        :text="isDeleting ? 'Deleting...' : 'Delete'"
        variant="danger"
        :disabled="isDeleting || !isConfirmationValid"
        :loading="isDeleting"
        @click="confirmDelete"
        aria-describedby="delete-button-help"
      />
    </template>
  </BaseModal>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import type { Exam } from '@/services/examService'
import { useToastService } from '@/services/toast'
import BaseModal from '@/components/common/BaseModal.vue'
import BaseButton from '@/components/common/BaseButton.vue'
import { Trash2 } from 'lucide-vue-next'

// Props
const props = defineProps<{
  isVisible: boolean
  exam: Exam | null
}>()

// Emits
const emit = defineEmits<{
  close: []
  success: []
}>()

// Toast service
const toast = useToastService()

// State
const isDeleting = ref(false)
const confirmationText = ref('')
const isConfirmationValid = ref(false)

// Methods
const isTextTruncated = (text: string): boolean => {
  return text.length > 40
}

const truncateText = (text: string): string => {
  if (text.length > 40) {
    return text.substring(0, 40) + '...'
  }
  return text
}

const validateConfirmation = () => {
  isConfirmationValid.value = confirmationText.value.trim() === 'DELETE'
}

// Handle Enter key press
const handleKeyPress = (event: KeyboardEvent) => {
  if (event.key === 'Enter' && props.isVisible && isConfirmationValid.value && !isDeleting.value) {
    event.preventDefault()
    confirmDelete()
  }
}

const confirmDelete = () => {
  if (isDeleting.value) return
  handleDelete()
}

const handleDelete = async () => {
  if (!props.exam || !isConfirmationValid.value || isDeleting.value) return

  isDeleting.value = true

  try {
    // Import the examService dynamically to avoid circular imports
    const { examService } = await import('@/services/examService')

    // Delete the exam permanently from the database
    await examService.deleteExam(props.exam.exam_id)

    toast.success(`Exam "${props.exam.exam_name}" has been permanently removed from the system.`)

    // Emit success event
    emit('success')
    closeModal()
  } catch (error: any) {
    console.error('Error deleting exam:', error)
    const errorMessage =
      error.response?.data?.message || error.message || 'Failed to delete exam'
    toast.error('Delete Failed', errorMessage)
  } finally {
    isDeleting.value = false
  }
}

const closeModal = () => {
  // Reset state
  isDeleting.value = false
  confirmationText.value = ''
  isConfirmationValid.value = false

  emit('close')
}

// Lifecycle hooks
onMounted(() => {
  document.addEventListener('keydown', handleKeyPress)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeyPress)
})
</script>

<style scoped>
</style>